/**
 * EngagementDashboard Component
 * Composition component for the full engagement report
 *
 * Story 3.6: Engagement Report Page Assembly
 */

import React, { useState, useEffect } from 'react';
import { clsx } from 'clsx';
import { RefreshCw } from 'lucide-react';
import { Button } from '../../common';
import type { DateRange } from '../DateRangeSelector';
import { EngagementReportSkeleton } from '../ReportLoadingSkeleton';
import { ReportEmptyState, ReportErrorState } from '../ReportEmptyState';
import { ScopeBadge } from '../ReportScopeIndicator';
import { toReportFilterParams, type ReportFilters } from '../ReportFilterPanel';
import { UserActivityTable } from './UserActivityTable';
import { CheckInComplianceCard } from './CheckInComplianceCard';
import { CollaborationMetricsCard } from './CollaborationMetricsCard';
import { FeatureUsageCard } from './FeatureUsageCard';
import { ActiveUsersCard } from './ActiveUsersCard';
import {
  useEngagementReportData,
  useUserActivityWithScope,
} from '../../../hooks/useReportData';
import type { ActivityLevel } from '../../../services/reportApi';
import { useReportPermissions } from '../../../hooks/useReportPermissions';
import { format } from 'date-fns';

/**
 * Roles the API lets search by email address. Mirrors EMAIL_PRIVILEGED_ROLES in
 * backend/src/utils/emailVisibility.ts; the backend remains authoritative — this
 * only decides whether the affordance is advertised.
 */
const CAN_SEARCH_BY_EMAIL = new Set(['account_admin', 'super_admin']);

export interface EngagementDashboardProps {
  /** Date range for the report (controlled by parent) */
  dateRange: DateRange;
  /**
   * Department / team / owner filters, owned by ReportsPage. Every card narrows to the people
   * they match (the backend's `resolveReportPeople`).
   */
  filters?: ReportFilters;
  /** Click handler for user */
  onUserClick?: (userId: string) => void;
  /** Click handler for OKR */
  onOKRClick?: (okrId: string) => void;
  /** Additional class names */
  className?: string;
}

/**
 * Engagement Dashboard Component
 */
export const EngagementDashboard: React.FC<EngagementDashboardProps> = ({
  dateRange,
  filters,
  onUserClick,
  onOKRClick,
  className,
}) => {
  const { userRole } = useReportPermissions();

  // User activity table state
  const [userActivityPage, setUserActivityPage] = useState(1);
  const [userActivitySort, setUserActivitySort] = useState<{ by: string; order: 'asc' | 'desc' }>({
    by: 'activityScore',
    order: 'desc'
  });
  const [userActivitySearch, setUserActivitySearch] = useState('');
  const [userActivityLevelFilter, setUserActivityLevelFilter] = useState<ActivityLevel | undefined>();

  // Reset pagination when the date range or the filters change: page 3 of a narrowed list may
  // not exist.
  useEffect(() => {
    setUserActivityPage(1);
  }, [dateRange, filters]);

  // Format date range params for API calls
  const dateRangeParams = {
    startDate: format(dateRange.startDate, 'yyyy-MM-dd'),
    endDate: format(dateRange.endDate, 'yyyy-MM-dd'),
  };

  const apiFilters = toReportFilterParams(filters);

  // Fetch engagement report data
  const engagementData = useEngagementReportData(dateRangeParams, apiFilters);

  // Fetch user activity with pagination/sorting
  const { query: userActivity, scope: userActivityScope } = useUserActivityWithScope(
    dateRangeParams,
    {
      page: userActivityPage,
      pageSize: 10,
      sortBy: userActivitySort.by,
      sortOrder: userActivitySort.order,
      search: userActivitySearch || undefined,
      activityLevel: userActivityLevelFilter,
    },
    true,
    apiFilters
  );

  // Handle refresh
  const handleRefresh = () => {
    engagementData.checkInCompliance.refetch();
    engagementData.collaborationMetrics.refetch();
    engagementData.featureUsage.refetch();
    engagementData.activeUsers.refetch();
    userActivity.refetch();
  };

  // Handle user activity table actions
  const handleUserActivityPageChange = (page: number) => {
    setUserActivityPage(page);
  };

  const handleUserActivitySortChange = (sortBy: string, sortOrder: 'asc' | 'desc') => {
    setUserActivitySort({ by: sortBy, order: sortOrder });
    setUserActivityPage(1);
  };

  const handleUserActivitySearchChange = (query: string) => {
    setUserActivitySearch(query);
    setUserActivityPage(1);
  };

  const handleUserActivityLevelFilterChange = (level?: ActivityLevel) => {
    setUserActivityLevelFilter(level);
    setUserActivityPage(1);
  };

  // Check for loading state
  if (engagementData.isLoading && userActivity.isLoading) {
    return (
      <div className={className}>
        <EngagementReportSkeleton />
      </div>
    );
  }

  // Check for error state
  if (engagementData.isError) {
    return (
      <div className={className}>
        <ReportErrorState
          error={engagementData.error instanceof Error ? engagementData.error : new Error('Failed to load engagement report')}
          onRetry={handleRefresh}
        />
      </div>
    );
  }

  return (
    <div className={clsx('space-y-6', className)}>
      {/* Header with refresh button */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold text-stone-900">Engagement Report</h2>
          <p className="text-stone-600 mt-1">Track user engagement and platform adoption</p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleRefresh}
          disabled={engagementData.isLoading}
        >
          <RefreshCw className={clsx('w-4 h-4 mr-2', engagementData.isLoading && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      {/* Active Users Overview — organization-wide even for a scoped caller */}
      {engagementData.activeUsers.data && (
        <ActiveUsersCard
          data={engagementData.activeUsers.data}
          isLoading={engagementData.activeUsers.isLoading}
          scope={engagementData.scopes.activeUsers}
        />
      )}

      {/* Metrics Cards Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Check-in Compliance */}
        {engagementData.checkInCompliance.data && (
          <CheckInComplianceCard
            data={engagementData.checkInCompliance.data}
            isLoading={engagementData.checkInCompliance.isLoading}
            scope={engagementData.scopes.checkInCompliance}
            orgBaseline={engagementData.checkInCompliance.data.orgBaseline}
          />
        )}

        {/* Collaboration Metrics — two scopes on one card, see the card's docs */}
        {engagementData.collaborationMetrics.data && (
          <CollaborationMetricsCard
            data={engagementData.collaborationMetrics.data}
            isLoading={engagementData.collaborationMetrics.isLoading}
            listScope={engagementData.scopes.collaborationLists}
            totalsScope={engagementData.scopes.collaborationTotals}
            onContributorClick={onUserClick}
            onOKRClick={onOKRClick}
          />
        )}

        {/* Feature Usage — organization-wide even for a scoped caller */}
        {engagementData.featureUsage.data && (
          <FeatureUsageCard
            data={engagementData.featureUsage.data}
            isLoading={engagementData.featureUsage.isLoading}
            scope={engagementData.scopes.featureUsage}
          />
        )}
      </div>

      {/* User Activity Table */}
      <div>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <h2 className="text-lg font-semibold text-stone-900">User Activity Breakdown</h2>
          <ScopeBadge scope={userActivityScope} />
        </div>
        {userActivityScope.isSelfOnly && (
          <p className="mb-3 text-sm text-stone-500">{userActivityScope.description}</p>
        )}
        {userActivity.data ? (
          <UserActivityTable
            data={userActivity.data.users}
            total={userActivity.data.total}
            page={userActivity.data.page}
            pageSize={userActivity.data.pageSize}
            totalPages={userActivity.data.totalPages}
            sortBy={userActivitySort.by}
            sortOrder={userActivitySort.order}
            searchQuery={userActivitySearch}
            activityLevelFilter={userActivityLevelFilter}
            canSearchByEmail={CAN_SEARCH_BY_EMAIL.has(userRole ?? '')}
            isLoading={userActivity.isLoading}
            onPageChange={handleUserActivityPageChange}
            onSortChange={handleUserActivitySortChange}
            onSearchChange={handleUserActivitySearchChange}
            onActivityLevelFilterChange={handleUserActivityLevelFilterChange}
            onUserClick={onUserClick}
          />
        ) : (
          <ReportEmptyState
            title="No User Activity Data"
            description="There is no user activity data available for the selected period."
          />
        )}
      </div>
    </div>
  );
};

export default EngagementDashboard;
